import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Minus, Lightbulb } from "lucide-react";

const SEV_BG = { high: "bg-red-50 border-red-200 text-red-800", medium: "bg-amber-50 border-amber-200 text-amber-800", low: "bg-blue-50 border-blue-200 text-blue-800" };
const TREND_ICON = { up: TrendingUp, down: TrendingDown, stable: Minus };

export default function AIInsightsPanel({ patientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const fetchInsights = async (refresh = false) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/patients/${patientId}/ai-insights`, { refresh });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not generate AI insights right now.");
    } finally { setBusy(false); }
  };

  useEffect(() => { fetchInsights(false); }, [patientId]);

  return (
    <div className="px-4 py-4 border-b border-violet-100 bg-gradient-to-br from-violet-50/60 via-white to-white" data-testid="ai-insights-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-violet-600 text-white grid place-items-center shadow-sm">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-stone-900 text-base leading-tight">AI Insights</h3>
            <p className="text-[11px] text-stone-500 leading-tight">Patterns &amp; recommendations from this patient's side-effect log. Powered by Claude Sonnet 4.5.</p>
          </div>
        </div>
        <button
          onClick={() => fetchInsights(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-violet-700 text-xs font-semibold disabled:opacity-60"
          data-testid="ai-insights-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />{busy ? "Analysing…" : "Re-analyse"}
        </button>
      </div>

      {err && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex items-start gap-2" data-testid="ai-insights-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      {!err && !data && busy && (
        <div className="rounded-lg bg-white border border-violet-100 px-3 py-3 text-sm text-stone-500 italic">Generating insights from this patient's logs…</div>
      )}

      {data && (
        <div className="space-y-3" data-testid="ai-insights-content">
          {data.summary && (
            <div className="rounded-lg bg-white border border-violet-100 px-3 py-2.5 text-sm text-stone-800 leading-relaxed">
              {data.summary}
              {data.from_cache && <span className="ml-2 text-[10px] uppercase tracking-wide text-stone-400">(cached)</span>}
            </div>
          )}

          {(data.patterns?.length > 0 || data.trends?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {data.patterns?.length > 0 && (
                <div className="rounded-lg bg-white border border-violet-100 px-3 py-2.5" data-testid="ai-insights-patterns">
                  <div className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold mb-1.5">Patterns</div>
                  <ul className="space-y-1.5">
                    {data.patterns.slice(0, 5).map((p, i) => (
                      <li key={i} className="text-xs text-stone-700"><b>{p.title}</b> — {p.detail}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.trends?.length > 0 && (
                <div className="rounded-lg bg-white border border-violet-100 px-3 py-2.5" data-testid="ai-insights-trends">
                  <div className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold mb-1.5">Trends</div>
                  <ul className="space-y-1.5">
                    {data.trends.slice(0, 5).map((t, i) => {
                      const Icon = TREND_ICON[t.direction] || Minus;
                      return (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-stone-700">
                          <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${t.direction === "up" ? "text-red-500" : t.direction === "down" ? "text-emerald-500" : "text-stone-400"}`} />
                          <span><b>{t.title}</b> — {t.detail}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {data.recommendations?.length > 0 && (
            <div className="rounded-lg bg-white border border-emerald-200 px-3 py-2.5" data-testid="ai-insights-recommendations">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1.5 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" />Recommendations for the care team</div>
              <ul className="space-y-1 list-disc list-inside text-xs text-stone-700">
                {data.recommendations.slice(0, 6).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {data.risk_flags?.length > 0 && (
            <div className="space-y-1.5" data-testid="ai-insights-risk-flags">
              {data.risk_flags.slice(0, 4).map((f, i) => (
                <div key={i} className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${SEV_BG[f.severity] || SEV_BG.medium}`}>
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span><b>{f.label}</b> ({f.severity}) — {f.why}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
